// AppContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  onAuthStateChanged,
} from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import firebaseConfig from "./firebaseConfig";
import { contentFetch } from "./learnerApi";

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

export const AppContext = createContext();

const STORAGE_KEYS = {
  USER_LOGIN: "userLoginState",
  ADMIN_LOGIN: "adminLoginState",
  USER_DATA: "userData",
  ANONYMOUS_ID: "anonymousId",
};

export const AppProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [userData, setUserData] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [showHeader] = useState(true);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

  // --- Load localStorage khi khởi động ---
  useEffect(() => {
    try {
      const savedUserLogin = localStorage.getItem(STORAGE_KEYS.USER_LOGIN);
      if (savedUserLogin) {
        const { isLoggedIn, username } = JSON.parse(savedUserLogin);
        if (isLoggedIn && username) {
          setIsLoggedIn(true);
          setUsername(username);
        }
      }

      const savedAdminLogin = localStorage.getItem(STORAGE_KEYS.ADMIN_LOGIN);
      if (savedAdminLogin) {
        const { isAdminLoggedIn, adminUsername } = JSON.parse(savedAdminLogin);
        if (isAdminLoggedIn && adminUsername) {
          setIsAdminLoggedIn(true);
          setAdminUsername(adminUsername);
        }
      }

      const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (savedUserData) {
        setUserData(JSON.parse(savedUserData));
      }
    } catch (error) {
      console.error("Lỗi khi đọc localStorage:", error);
      localStorage.clear();
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // --- Đồng bộ localStorage khi thay đổi ---
  useEffect(() => {
    if (!isInitialized) return;

    try {
      isLoggedIn && username
        ? localStorage.setItem(
            STORAGE_KEYS.USER_LOGIN,
            JSON.stringify({
              isLoggedIn,
              username,
            })
          )
        : localStorage.removeItem(STORAGE_KEYS.USER_LOGIN);

      isAdminLoggedIn && adminUsername
        ? localStorage.setItem(
            STORAGE_KEYS.ADMIN_LOGIN,
            JSON.stringify({
              isAdminLoggedIn,
              adminUsername,
            })
          )
        : localStorage.removeItem(STORAGE_KEYS.ADMIN_LOGIN);

      userData
        ? localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
        : localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch (err) {
      console.error("Lỗi khi ghi localStorage:", err);
    }
  }, [
    isLoggedIn,
    username,
    isAdminLoggedIn,
    adminUsername,
    userData,
    isInitialized,
  ]);

  // --- Logic thống kê người dùng mới ---
  const sendUserActivity = useCallback(
    async (userId = null, anonymousId = null) => {
      const today = new Date().toISOString().split("T")[0];
      const localStorageKey = userId
        ? `lastLoggedDate_${userId}`
        : `lastLoggedDate_${anonymousId}`;
      const lastLoggedDate = localStorage.getItem(localStorageKey);

      if (lastLoggedDate !== today) {
        try {
          const body = { userId, anonymousId };
          const response = await fetch(`${API_BASE_URL}/user-activity`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });

          if (response.ok) {
            localStorage.setItem(localStorageKey, today);
            console.log("User activity logged for the day.");
            if (userId && anonymousId) {
              localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_ID);
            }
          } else {
            console.error("Failed to log user activity.");
          }
        } catch (error) {
          console.error("Error sending user activity:", error);
        }
      } else {
        console.log("User activity for today already logged.");
      }
    },
    [API_BASE_URL]
  );

  // --- Lấy dữ liệu người dùng từ backend ---
  const fetchUserData = useCallback(async () => {
    setIsUserLoading(true); // bắt đầu tải
    try {
      const user = auth.currentUser;
      if (!user) {
        setUserData(null);
        return null;
      }

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/users/${user.uid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Không lấy được thông tin người dùng");

      const data = await res.json();
      setUserData(data);
      return data;
    } catch (err) {
      console.error("Lỗi fetchUserData:", err);
      setUserData(null);
      return null;
    } finally {
      setIsUserLoading(false); // kết thúc tải
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsUserLoading(true);
      let anonymousId = localStorage.getItem(STORAGE_KEYS.ANONYMOUS_ID);
      if (!anonymousId) {
        anonymousId = uuidv4();
        localStorage.setItem(STORAGE_KEYS.ANONYMOUS_ID, anonymousId);
      }

      if (user) {
        // ✅ Bổ sung kiểm tra xác minh email tại đây.
        // Chỉ ghi nhận hoạt động nếu email đã được xác minh.
        if (user.emailVerified) {
          await sendUserActivity(user.uid, anonymousId);
          fetchUserData();
        } else {
          // Nếu email chưa xác minh, không ghi nhận hoạt động
          // và chỉ fetchUserData nếu cần thiết
          setUserData(null);
          setIsUserLoading(false);
        }
      } else {
        sendUserActivity(null, anonymousId);
        setUserData(null);
        setIsUserLoading(false);
      }
    });

    return () => unsubscribe();
  }, [sendUserActivity, fetchUserData]);

  const updateUserData = useCallback((newData) => {
    setUserData(newData);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(newData));
  }, []);

  // --- Đăng nhập user ---
  const login = useCallback(async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        setShowVerificationMessage(true);
        await signOut(auth);
        return new Error("Vui lòng xác minh email trước khi đăng nhập.");
      }

      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified,
      };
    } catch (err) {
      console.error("Lỗi login:", err.message);
      return err;
    }
  }, []);

  const logout = useCallback(() => {
    signOut(auth)
      .then(() => {
        setIsLoggedIn(false);
        setUsername("");
        setUserData(null);
        localStorage.removeItem(STORAGE_KEYS.USER_LOGIN);
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      })
      .catch((err) => console.error("Lỗi logout:", err));
  }, []);

  // --- Đăng ký user ---
  const register = useCallback(async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      await sendEmailVerification(user);
      await signOut(auth);
      return null;
    } catch (err) {
      console.error("Lỗi register:", err.message);
      throw err;
    }
  }, []);

  // --- Hàm trừ xu ---
  const deductCoins = useCallback(
    async (amount) => {
      try {
        const user = auth.currentUser;
        if (!user) {
          console.error("Lỗi: Người dùng chưa đăng nhập.");
          return { success: false, message: "Người dùng chưa đăng nhập." };
        }

        const res = await fetch(
          `${API_BASE_URL}/users/${user.uid}/deductCoins`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${await user.getIdToken()}`,
            },
            body: JSON.stringify({ amount }),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          // Trường hợp lỗi từ server (ví dụ: số dư không đủ)
          console.error("Lỗi trừ xu:", data.message);
          return { success: false, message: data.message || "Lỗi khi trừ xu." };
        }

        // Cập nhật userData trong state và localStorage
        updateUserData(data.user);

        return { success: true, user: data.user };
      } catch (err) {
        console.error("Lỗi trừ xu:", err.message);
        return { success: false, message: "Lỗi kết nối hoặc lỗi hệ thống." };
      }
    },
    [API_BASE_URL, updateUserData]
  );

  // --- Đăng nhập admin ---
  const adminLogin = useCallback(async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        await signOut(auth);
        return new Error("Vui lòng xác minh email trước khi đăng nhập.");
      }

      const adminEmails = ["trungtammayxanh@gmail.com"];
      if (adminEmails.includes(user.email)) {
        setIsAdminLoggedIn(true);
        setAdminUsername(user.email);
        return null;
      } else {
        await signOut(auth);
        return new Error("Tài khoản này không có quyền admin.");
      }
    } catch (err) {
      console.error("Lỗi adminLogin:", err.message);
      return err;
    }
  }, []);

  const adminLogout = useCallback(() => {
    signOut(auth)
      .then(() => {
        setIsAdminLoggedIn(false);
        setAdminUsername("");
        localStorage.removeItem(STORAGE_KEYS.ADMIN_LOGIN);
      })
      .catch((err) => console.error("Lỗi adminLogout:", err));
  }, []);

  // --- Lấy câu hỏi từ backend ---
  // Trắc nghiệm chỉ dành cho học viên trung tâm: chưa mở khoá thì ném lỗi có code "LEARNER_REQUIRED"
  const fetchQuestions = useCallback(
    async (grade, exam, count) => {
      try {
        const response = await contentFetch(
          `${API_BASE_URL}/questions?grade=${grade}&exam=${exam}&count=${count}`
        );
        if (response.status === 401) {
          const err = new Error("Nội dung dành cho học viên trung tâm.");
          err.code = "LEARNER_REQUIRED";
          throw err;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
      } catch (error) {
        if (error.code === "LEARNER_REQUIRED") throw error;
        console.error("Lỗi khi lấy câu hỏi:", error);
        return [];
      }
    },
    [API_BASE_URL]
  );

  // Các hàm để xử lý dữ liệu Practice ---
  const fetchPractices = useCallback(async () => {
    try {
      const response = await contentFetch(`${API_BASE_URL}/practices`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Lỗi khi lấy danh sách bộ đề luyện thi:", error);
      return [];
    }
  }, [API_BASE_URL]);

  const fetchPracticeById = useCallback(
    async (id) => {
      try {
        const response = await contentFetch(`${API_BASE_URL}/practices/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Lỗi khi lấy chi tiết bộ đề luyện thi:", error);
        return null;
      }
    },
    [API_BASE_URL]
  );

  return (
    <AppContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        username,
        setUsername,
        userData,
        isUserLoading,
        updateUserData,
        fetchUserData,
        login,
        logout,
        register,
        isAdminLoggedIn,
        adminUsername,
        adminLogin,
        adminLogout,
        showHeader,
        showVerificationMessage,
        setShowVerificationMessage,
        API_BASE_URL,
        isInitialized,
        fetchQuestions,
        fetchPractices,
        fetchPracticeById,
        deductCoins,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
